import { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Carousel, ConfigProvider } from "antd";
import ItemCardSkeleton from "../../components/ItemCardSkeleton";

import "./style.css"
import { useSelector } from "react-redux";

import ProductsShowcase from "../../components/ProductsShowcase";
import translations from "../../components/LanguageModal/translations";
import { selectLanguage as getLanguage } from "../../features/language/selectors";
import AdvantageCard from "../../components/AdvantageCard";
import { categoriesSelectors } from "../../features/categories";
import { fetchBannersList, bannersSelectors } from '../../features/banners';
import { fetchCatalogue, selectCatalogueCategoryIds } from '../../features/catalogue';

import { ReactComponent as SupportIcon } from "../../icons/support-icon.svg";
import { ReactComponent as DeliveryIcon } from "../../icons/delivery-icon.svg";
import { ReactComponent as PartnershipIcon } from "../../icons/partnership-icon.svg";
import { ReactComponent as QualityIcon } from "../../icons/quality-icon.svg";
import { ReactComponent as AdminPanelIcon } from "../../icons/admin-panel.svg";
import { ReactComponent as ConstructionIcon } from "../../icons/construction.svg";

import {
    fetchCategoriesList,
} from "../../features/categories";
import {isMobile} from "react-device-detect";

import {fetchWidgetsList, widgetsSelectors} from '../../features/widgets';
import {selectAdvantages} from "../../features/widgets/selectors";
import { geoSelectors } from '../../features/geolocation';

const advantageIcons = [
    <SupportIcon />,
    <DeliveryIcon />,
    <PartnershipIcon />,
    <QualityIcon />,
    <AdminPanelIcon />,
    <ConstructionIcon />,
];

const getInternalPath = (url) => {
    if (!url) return null;

    try {
        const parsed = new URL(url, window.location.origin);

        if (parsed.hostname.replace(/^www\./, '') !== window.location.hostname.replace(/^www\./, '')) {
            return null;
        }

        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (e) {
        return null;
    }
};

const CarouselSkeleton = () => {
    return (
        <div className="skeleton-shimmer" style={{
            margin: '0',
            borderRadius: '42px',
            overflow: 'hidden',
            marginBottom: '70px',
            minHeight: '556px',
        }} />
    );
};

const AdvantagesSkeleton = () => {
    return (
        <div className='advantages'>
            <div className='advantages-title'>
                <div className="skeleton-shimmer" style={{ width: 250, height: 28, borderRadius: 6 }} />
            </div>
            <div className='advantages-grid'>
                {Array(4).fill(null).map((_, i) => (
                    <div key={i} style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '12px',
                        padding: '24px',
                        display: 'flex',
                        gap: 16,
                        alignItems: 'flex-start',
                    }}>
                        <div className="skeleton-shimmer" style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0 }} />
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className="skeleton-shimmer" style={{ width: '60%', height: 14, borderRadius: 6 }} />
                            <div className="skeleton-shimmer" style={{ width: '100%', height: 12, borderRadius: 6 }} />
                            <div className="skeleton-shimmer" style={{ width: '80%', height: 12, borderRadius: 6 }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function Home(props) {
    const dispatch = useDispatch();
    const [itemsPerPageProducts, setItemsPerPageProducts] = useState(5);
    const [itemsPerPageSellers, setItemsPerPageSellers] = useState(8);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const banners = useSelector(bannersSelectors.selectAllBanners);
    const isBannersLoading = useSelector(bannersSelectors.selectIsBannersLoading);

    const catalogueCategoryIds = useSelector(selectCatalogueCategoryIds);

    const {
        selectCategoriesById,
    } = categoriesSelectors;

    const {
        selectWidgetsState,
        selectAllWidgets,
    } = widgetsSelectors;

    const categoriesList = useSelector(state => selectCategoriesById(state));

    const widgetsList = useSelector(state => selectWidgetsState(state));
    const advantages = useSelector(selectAdvantages)?.widgets;

    const regionCode = useSelector(geoSelectors.selectRegionCode);

    useEffect(() => {
        const loadInitialData = async () => {
            setIsInitialLoading(true);

            await Promise.all([
                dispatch(fetchBannersList({ per_page: 10, refresh: true })),
                dispatch(fetchCatalogue()),
                dispatch(fetchCategoriesList({ refresh: true })),
                dispatch(fetchWidgetsList()),
            ]);

            setTimeout(() => {
                setIsInitialLoading(false);
            }, 300);
        };

        loadInitialData();
    }, [itemsPerPageProducts, regionCode, dispatch]);

    useEffect(() => {
        const updateItemsPerPage = () => {
            if (window.innerWidth > 1400) {
                setItemsPerPageProducts(5)
                setItemsPerPageSellers(8)
            } else if (window.innerWidth > 1200) {
                setItemsPerPageProducts(4)
                setItemsPerPageSellers(8)
            } else if (window.innerWidth > 768) {
                setItemsPerPageProducts(3)
                setItemsPerPageSellers(6)
            } else if (window.innerWidth > 480) {
                setItemsPerPageProducts(2)
                setItemsPerPageSellers(4)
            } else {
                setItemsPerPageProducts(1)
                setItemsPerPageSellers(2)
            }
        }

        updateItemsPerPage()
        window.addEventListener('resize', updateItemsPerPage)
        return () => window.removeEventListener('resize', updateItemsPerPage)
    }, [])

    const language = useSelector(getLanguage);
    const t = (key) => {
        const languageString = language[0] + language[1];
        const translation = translations[languageString];
        return translation ? translation[key] || key : key;
    };

    const renderBanner = (banner) => {
        const mobileImage = banner?.images?.mobile;
        const desktopImage = banner?.images?.desktop;
        const imageUrl = isMobile ? mobileImage : desktopImage;
        const url = banner?.url;
        const internalPath = getInternalPath(url);

        const image = <img src={imageUrl} alt={banner?.title || ''} className='carousel-item-img' />;

        return (
            <div key={banner.id}>
                {internalPath ? (
                    <Link to={internalPath} className='carousel-item-1'>
                        {image}
                    </Link>
                ) : url ? (
                    <a
                        href={url}
                        className='carousel-item-1'
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {image}
                    </a>
                ) : (
                    <div className='carousel-item-1'>
                        {image}
                    </div>
                )}
            </div>
        );
    };

    if (isInitialLoading) {
        return (
            <div className="page-content">
                <CarouselSkeleton />

                <div style={{ margin: '0 0 74px 0' }}>
                    <div className='showcase'>
                        <div className="showcase-header">
                            <div className="showcase-title main">
                                <div className="skeleton-shimmer" style={{ width: 200, height: 20, borderRadius: 6 }} />
                            </div>
                            <div className='showcase-description'>
                                <div className="skeleton-shimmer" style={{ width: 150, height: 16, borderRadius: 6 }} />
                            </div>
                            <div className="showcase-header-ctn-btn">
                                <div className="skeleton-shimmer" style={{ width: 183, height: 30, borderRadius: 5 }} />
                                <div className="showcase-ctn-btn-arrow" style={{ marginTop: '9px' }}>
                                    <div className="skeleton-shimmer" style={{ width: 27, height: 27, borderRadius: '50%' }} />
                                    <div className="skeleton-shimmer" style={{ width: 27, height: 27, borderRadius: '50%' }} />
                                </div>
                            </div>
                        </div>
                        <div className="showcase-container">
                            {Array(itemsPerPageProducts).fill(null).map((_, i) => (
                                <ItemCardSkeleton key={i} />
                            ))}
                        </div>
                    </div>
                </div>

                <AdvantagesSkeleton />
            </div>
        );
    }

    return (
        <div className="page-content">
            <ConfigProvider
                theme={{
                    components: {
                        Carousel: {
                            arrowSize: 50,
                        },
                    },
                }}
            >
                <Carousel
                    autoplay={{ dotDuration: true }}
                    arrows
                    adaptiveHeight
                    draggable
                    swipe
                    swipeToSlide
                    touchMove
                    pauseOnHover
                    autoplaySpeed={5000}
                    speed={800}
                    dots={true}
                    dotPosition="bottom"
                    className="home-banner-carousel"
                >
                    {banners && banners.length > 0 ? (
                        banners.map(banner => renderBanner(banner))
                    ) : (
                        <div className='carousel-item-1'>
                            <div className='carousel-item-text'>
                                Эффективное решение для вашего бизнеса
                            </div>
                        </div>
                    )}
                </Carousel>
            </ConfigProvider>

                {catalogueCategoryIds.map(catId => (
                    <ProductsShowcase
                        key={catId}
                        categoryId={catId}
                        title={categoriesList[catId]?.name}
                        titleStyle='main'
                        description={categoriesList[catId]?.description}
                        style={{ margin: '0 0 74px 0' }}
                        itemsPerPage={itemsPerPageProducts}
                    />
                ))}

                <div className='advantages'>
                    <div className='advantages-title'>
                        {advantages.heading.text}
                    </div>
                    <div className='advantages-grid'>
                        {
                            advantages.advantages.map((advantage, index) => (
                                <AdvantageCard
                                    key={index}
                                    icon={advantageIcons[index] || advantageIcons[0]}
                                    title={advantage.title}
                                    desc={[
                                        ...advantage.features,
                                    ]}
                                />
                            ))
                        }
                    </div>
                </div>
        </div>
    );
}